import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBackroute } from '../../../contexts/BackrouteContext';
import ExitModal from '../../Common/ExitModal';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

import BenefitCard from '../../../v2/Benefits/Beneficiary/Components/BenefitCard';
import FirmDetails from '../../../v2/Benefits/Beneficiary/Components/FirmDetails';
import IconReusable from '../../../components/IconReusable';
import {
  TextField,
  CustomSelect,
  CustomCheckbox,
  CustomButton,
  DatePicker,
} from '../../../v2/MUI_custom_component';
import { toast } from 'react-toastify';
import { submitBeneficiaryNominee } from './APIs/action';

const MOCK_INSURANCE_DATA = {
  benefit_card: {
    name: 'Golden Age Plan',
    value: 'Golden Age Plan',
    logo_url:
      'https://d2djimvzddgguw.cloudfront.net/humbee-benefit-icons/gap.svg',
    description: 'Retire with confidence, live with freedom',
    status: {
      label: 'Inactive',
      color: '#CC0000',
    },
  },
  note: 'Ensure that the bank account details belong to your registered firm — CGG INFRA INDIA PRIVATE LIMITED.',
  firm_pan: null,
  selected_firm: [
    {
      display_name: 'Firm Name',
      display_value: 'CGG INFRA INDIA PRIVATE LIMITED',
    },
    {
      display_name: 'Firm Type',
      display_value: 'Private Limited Company',
    },
    {
      display_name: 'GST',
      display_value: '33AAKCC8525B1ZX',
    },
  ],
  owner_type_text: {
    label: "Director's Name",
    value: 'Private Limited Company',
  },
  owner_list: [
    { label: 'RAMAMURTHI CHITHRA ', value: 'RAMAMURTHI CHITHRA ' },
    { label: 'C M GOWSALYA ', value: 'C M GOWSALYA ' },
    { label: 'VENUGOPAL KARTHIK ', value: 'VENUGOPAL KARTHIK ' },
  ],
  relations: [
    { label: 'Father', value: 'Father' },
    { label: 'Mother', value: 'Mother' },
    { label: 'Son', value: 'Son' },
    { label: 'Daughter', value: 'Daughter' },
    { label: 'Son-in-law', value: 'Son-in-law' },
    { label: 'Daughter-in-law', value: 'Daughter-in-law' },
    { label: 'Grand-Son', value: 'Grand-Son' },
    { label: 'Grand-Daughter', value: 'Grand-Daughter' },
  ],
  confirmation_text: {
    label:
      'This is to certify that the names submitted for the purpose of availing the applicable benefits have been duly reviewed, approved, and authorized by all the members/partners/directors of the firm/company/organisation in accordance with its internal corporate policies and governance framework. The undersigned, being an authorized representative of the firm/company/organisation, hereby confirms the authenticity and validity of the said approvals.',
    show: true,
  },
  confirmation_text_for_digilocker: {
    label: 'I accept the KYC Consent',
    show: true,
  },
  kyc_consent_data: [
    'I, hereby authorize Twenty Point Nine Five Ventures Private Limited (HUMBEE) to download my KYC records from the Central KYC Records Registry (CKYCR)/Digilocker. I understand that this information will be shared with National Pension System - Point Of Presence (NPS - POP) and Central Recordkeeping Agency - NPS (CRA - NPS) for KYC verification and compliance with regulatory requirements for NPS registration.',
    'I acknowledge that the information contained is accurate to the best of my knowledge and authorize Twenty Point Nine Five Ventures Private Limited (HUMBEE) to use this information for the aforementioned purposes.',
    'In case any of the above information is found to be false, untrue, misleading, or misrepresenting, I am aware that I may be held liable for it.',
  ],
  existing_beneficiaries: [],
};

const AddGapNominee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const insuranceData = location?.state?.insuranceData || MOCK_INSURANCE_DATA;
  const { updateBackroute, resetBackroute } = useBackroute();

  const beneficiary_details = useSelector(
    (state) => state?.gapBeneficiaryReducer || {},
  );
  const { selectedEntity: company_esi_id } = useSelector(
    (state) => state.entityReducer,
  );

  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    updateBackroute({ customHandler: () => setShowExitModal(true) });
    return () => resetBackroute();
  }, []);

  const [nomineeForm, setNomineeForm] = useState({
    relation: '',
    full_name: '',
    gender: '',
    dob: '',
    age: '',
  });
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field, value) => {
    if (field === 'dob') {
      const birthDate = dayjs(value);
      const age = birthDate.isValid() ? dayjs().diff(birthDate, 'year') : '';
      setNomineeForm((prev) => ({
        ...prev,
        dob: value,
        age: age !== '' ? String(age) : '',
      }));
    } else {
      setNomineeForm((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!nomineeForm.relation) newErrors.relation = 'Relation is required';
    if (!nomineeForm.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!nomineeForm.gender) newErrors.gender = 'Gender is required';
    if (!nomineeForm.dob) newErrors.dob = 'Date of birth is required';
    if (!confirmed) newErrors.confirmed = 'Please confirm to proceed';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      version: 'v2',
      id_type: 1,
      company_esi_id,
      insurance_id: insuranceData?.benefit_card?.name,
      firm_owner_name: beneficiary_details?.firm_owner_name,
      firm_type: beneficiary_details?.firm_type,
      beneficiary: beneficiary_details?.beneficiary,
      nominee: {
        full_name: nomineeForm.full_name,
        gender: nomineeForm.gender,
        dob: nomineeForm.dob,
        relation: nomineeForm.relation,
      },
    };
    
    const { data, message, error } = await submitBeneficiaryNominee(payload);
    setSubmitting(false);
    if (!error) {
      toast.success(message || 'Gap beneficiary added successfully');
      navigate('/beneficiary');
    } else {
      toast.error(message);
    }
  };

  const isSubmitDisabled =
    !nomineeForm.relation ||
    !nomineeForm.full_name.trim() ||
    !nomineeForm.gender ||
    !nomineeForm.dob ||
    !confirmed;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2 }}>
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ alignItems: 'stretch', mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <BenefitCard
              item={insuranceData?.benefit_card}
              sx={{ height: '100%' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                boxShadow: 'none',
                p: 2,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
              }}
            >
              {insuranceData?.selected_firm &&
                insuranceData.selected_firm.map((detail, i) => (
                  <React.Fragment key={i}>
                    <FirmDetails detail={detail} />
                  </React.Fragment>
                ))}
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Beneficiary Details */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ background: '#fff', gap: 2, borderRadius: 2, p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  borderRadius: 2,
                  background: '#fff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <Typography variant='h2'>Beneficiary Details</Typography>
                  <IconButton
                    size='small'
                    sx={{ color: '#995A00' }}
                    onClick={() => navigate(-1)}
                  >
                    <IconReusable name='Delete' color='#995A00' />
                  </IconButton>
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                  {[
                    { label: 'Name', value: beneficiary_details?.beneficiary?.full_name },
                    { label: 'Gender', value: beneficiary_details?.beneficiary?.gender },
                    { label: 'D.O.B', value: beneficiary_details?.beneficiary?.dob },
                    { label: 'Mobile No.', value: beneficiary_details?.beneficiary?.phone_number },
                    { label: 'PAN', value: beneficiary_details?.beneficiary?.pan },
                  ].map(({ label, value }) => (
                    <Box
                      key={label}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}
                    >
                      <Typography variant='labelD' sx={{ color: '#666666' }}>
                        {label}
                      </Typography>
                      <Typography variant='p3' sx={{ textAlign: 'right' }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Nominee Form */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography variant='h2'>Add Nominee Details</Typography>

                {/* Relation */}
                <CustomSelect
                  id='nominee-relation-select'
                  label='Relation*'
                  value={nomineeForm.relation}
                  onChange={(e) => handleFieldChange('relation', e.target.value)}
                  error={!!errors.relation}
                  helperText={errors.relation}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value='' disabled>
                    Select Relation
                  </MenuItem>
                  {(insuranceData?.relations || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </CustomSelect>

                {/* Full Name */}
                <TextField
                  label='Full Name*'
                  value={nomineeForm.full_name}
                  onChange={(e) => handleFieldChange('full_name', e.target.value)}
                  error={!!errors.full_name}
                  helperText={errors.full_name}
                  placeholder='Enter Full Name'
                  fullWidth
                />

                {/* Gender */}
                <CustomSelect
                  id='nominee-gender-select'
                  label='Gender*'
                  value={nomineeForm.gender}
                  onChange={(e) => handleFieldChange('gender', e.target.value)}
                  error={!!errors.gender}
                  helperText={errors.gender}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value='' disabled>
                    Select Gender
                  </MenuItem>
                  <MenuItem value='MALE'>Male</MenuItem>
                  <MenuItem value='FEMALE'>Female</MenuItem>
                </CustomSelect>

                {/* DOB + Age row */}
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label='Date of Birth*'
                        value={nomineeForm.dob ? dayjs(nomineeForm.dob) : null}
                        onChange={(newValue) => {
                          const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                          handleFieldChange('dob', dateString);
                        }}
                        error={!!errors.dob}
                        helperText={errors.dob}
                        disableFuture
                        format='DD/MM/YYYY'
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box sx={{ width: '106px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography variant='labelD' sx={{ color: '#666666' }}>
                      Age
                    </Typography>
                    <Typography variant='p3'>
                      {nomineeForm.age ? `${nomineeForm.age}y` : '—'}
                    </Typography>
                  </Box>
                </Box>

                {/* Consent checkbox */}
                {insuranceData?.confirmation_text?.show && (
                  <Box>
                    <CustomCheckbox
                      label={insuranceData.confirmation_text.label}
                      checked={confirmed}
                      onChange={(e) => {
                        setConfirmed(e.target.checked);
                        if (e.target.checked && errors.confirmed) {
                          setErrors((prev) => {
                            const { confirmed: _, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                    />
                    {errors.confirmed && (
                      <Typography
                        variant='caption'
                        color='error'
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {errors.confirmed}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Submit */}
                <CustomButton
                  variant='filled'
                  label={submitting ? 'Submitting...' : 'Submit'}
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled || submitting}
                  fullWidth
                />
              </Box>
          </Grid>
        </Grid>
      </Box>
      <ExitModal
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={() => navigate('/beneficiary')}
        title="Are you sure you want to exit?"
        subtitle="This action cannot be undone. All data would be lost?"
        confirmLabel="Yes"
        cancelLabel="No"
      />
    </Box>
  );
};

export default AddGapNominee;
